// react
import React, { useCallback, useEffect, useRef, useState } from 'react';
import EmptyState from '../shared/EmptyState';

// third-party
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// application
// import Pagination from '../shared/Pagination';
import {
    Filters16Svg,
    LayoutGrid16x16Svg,
    LayoutList16x16Svg,
} from '../../svg';
import { sidebarOpen } from '../../store/sidebar';
import ProductCardKategori from '../shared/ProductCardKategori';
import ProductCardSkeleton from '../shared/ProductCardSkeleton';
import { Link } from 'react-router-dom';
import { prefetchProductDescriptions } from '../../services/productDescriptionService';
import { url } from '../../services/utils';

function useSetOption(option, filter, dispatch) {
    const callback = useCallback(filter, []);

    return useCallback((data) => {
        dispatch({
            type: 'SET_OPTION_VALUE',
            option,
            value: callback(data),
        });
    }, [option, callback, dispatch]);
}

function ProductsView(props) {
    const {
        isLoading,
        productsList,
        totalItem: propsTotalItem,
        hasMore,
        isLoadingMore,
        onLoadMore,
        options,
        filters,
        dispatch,
        layout: propsLayout,
        grid,
        offcanvas,
        sidebarOpen,
    } = props;
    const [layout, setLayout] = useState(
        propsLayout === 'grid-with-features' ? 'grid' : (propsLayout || 'grid')
    );

    const handleSortChange = useSetOption('sort', (event) => event.target.value, dispatch);
    const handleLimitChange = useSetOption('limit', (event) => parseFloat(event.target.value), dispatch);

    const handleResetFilters = useCallback(() => {
        dispatch({ type: 'RESET_FILTERS' });
    }, [dispatch]);

    const sentinelRef = useRef(null);

    // Prefetch product descriptions in the background
    useEffect(() => {
        if (Array.isArray(productsList) && productsList.length > 0) {
            prefetchProductDescriptions(productsList);
        }
    }, [productsList]);

    // Native, lightweight IntersectionObserver for lazy loading next 20 items
    useEffect(() => {
        if (!hasMore || isLoadingMore || !onLoadMore) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0] && entries[0].isIntersecting) {
                    onLoadMore();
                }
            },
            {
                root: null,
                rootMargin: '350px',
                threshold: 0,
            }
        );

        const currentEl = sentinelRef.current;
        if (currentEl) {
            observer.observe(currentEl);
        }

        return () => {
            if (currentEl) {
                observer.unobserve(currentEl);
            }
            observer.disconnect();
        };
    }, [hasMore, isLoadingMore, onLoadMore]);

    const filtersCount = filters ? Object.keys(filters).map((x) => filters[x]).filter((x) => x).length : 0;
    let viewModes = [
        { key: 'grid', title: 'Grid', icon: <LayoutGrid16x16Svg /> },
        { key: 'list', title: 'List', icon: <LayoutList16x16Svg /> },
    ];

    viewModes = viewModes.map((viewMode) => {
        const className = classNames('layout-switcher__button', {
            'layout-switcher__button--active': layout === viewMode.key,
        });

        return (
            <button
                key={viewMode.key}
                title={viewMode.title}
                type="button"
                className={className}
                onClick={() => setLayout(viewMode.key)}
            >
                {viewMode.icon}
            </button>
        );
    });

    const items = Array.isArray(productsList)
        ? productsList
        : (productsList && Array.isArray(productsList.items) ? productsList.items : []);

    const totalCount = propsTotalItem != null
        ? propsTotalItem
        : (productsList && productsList.total_item != null ? productsList.total_item : items.length);

    const productsListItems = items.map((produkkategori) => (
        <div key={produkkategori.id} className="products-list__item">
            <ProductCardKategori produkkategori={produkkategori} layout={layout} />
        </div>
    ));

    const rootClasses = classNames('products-view', {
        'products-view--loading': isLoading && items.length === 0,
    });

    const viewOptionsClasses = classNames('view-options', {
        'view-options--offcanvas--always': offcanvas === 'always',
        'view-options--offcanvas--mobile': offcanvas === 'mobile',
    });

    let content;

    if (items.length > 0) {
        content = (
            <div className="products-view__content">
                <div className="products-view__options">
                    <div className={viewOptionsClasses}>
                        <div className="view-options__legend">
                            <span className="view-options__legend-text">Menampilkan</span>
                            <span className="view-options__legend-badge">
                                {totalCount > 0 ? totalCount.toLocaleString('id-ID') : items.length}
                            </span>
                            <span className="view-options__legend-text">Produk</span>
                        </div>
                        <div className="view-options__layout">
                            <div className="layout-switcher">
                                <div className="layout-switcher__list">
                                    {viewModes}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className="products-view__list products-list"
                    data-layout={layout !== 'list' ? grid : layout}
                    data-with-features={layout === 'grid-with-features' ? 'true' : 'false'}
                >
                    <div className="products-list__body">
                        {productsListItems}
                        {isLoadingMore && (
                            Array.from({ length: 4 }, (_, idx) => (
                                <div key={`more-skeleton-${idx}`} className="products-list__item">
                                    <ProductCardSkeleton />
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Sentinel for infinite scroll / lazy loading */}
                {hasMore && <div ref={sentinelRef} className="products-view__sentinel" />}

                {/* End of list message */}
                {!hasMore && items.length > 0 && (
                    <div className="products-view__end-message">
                        <span>Semua {totalCount > 0 ? totalCount.toLocaleString('id-ID') : items.length} produk telah dimuat</span>
                    </div>
                )}
            </div>
        );
    } else if (isLoading) {
        const skeletons = Array.from({ length: 8 }, (_, idx) => (
            <div key={`init-skeleton-${idx}`} className="products-list__item">
                <ProductCardSkeleton />
            </div>
        ));

        content = (
            <div className="products-view__content">
                <div className="products-view__options">
                    <div className={viewOptionsClasses}>
                        <div className="view-options__legend">
                            <span className="view-options__legend-text">Menampilkan</span>
                            <span className="view-options__legend-badge">
                                <span className="skeleton-bone skeleton-bone--pill" style={{ width: 45, height: 16 }} />
                            </span>
                            <span className="view-options__legend-text">Produk</span>
                        </div>
                        <div className="view-options__layout">
                            <div className="layout-switcher">
                                <div className="layout-switcher__list">
                                    {viewModes}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className="products-view__list products-list"
                    data-layout={layout !== 'list' ? grid : layout}
                >
                    <div className="products-list__body">
                        {skeletons}
                    </div>
                </div>
            </div>
        );
    } else {
        content = (
            <EmptyState icon="search" title="Produk tidak ditemukan" text="Coba kata kunci atau kategori lain." actionLabel="Lihat Kategori" actionTo="/shop/category" actionIcon="th-large" />
        );
    }

    return (
        <div className={rootClasses}>
            <div className="products-view__loader" />
            {content}
        </div>
    );
}

ProductsView.propTypes = {
    /**
     * Indicates that products is loading.
     */
    isLoading: PropTypes.bool,
    /**
     * ProductsList object.
     */
    productsList: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    /**
     * Products list options.
     */
    options: PropTypes.object,
    /**
     * Products list filters.
     */
    filters: PropTypes.object,
    /**
     * Category page dispatcher.
     */
    dispatch: PropTypes.func,
    /**
     * products list layout (default: 'grid')
     * one of ['grid', 'grid-with-features', 'list']
     */
    layout: PropTypes.oneOf(['grid', 'grid-with-features', 'list']),
    /**
     * products list layout (default: 'grid')
     * one of ['grid-3-sidebar', 'grid-4-full', 'grid-5-full']
     */
    grid: PropTypes.oneOf(['grid-3-sidebar', 'grid-4-full', 'grid-5-full']),
    /**
     * indicates when sidebar should be off canvas
     */
    offcanvas: PropTypes.oneOf(['always', 'mobile']),
};

ProductsView.defaultProps = {
    layout: 'grid',
    grid: 'grid-3-sidebar',
    offcanvas: 'mobile',
};

const mapDispatchToProps = {
    sidebarOpen,
};

export default connect(
    () => ({}),
    mapDispatchToProps,
)(ProductsView);
